import VueRouter from 'vue-router';
import AuthManager from '../managers/AuthManager';
import AppChrome from '../components/chromes/AppChrome';
import TeamChrome from '../components/chromes/TeamChrome';
import AdminChrome from '../components/chromes/AdminChrome';
import Home from '../components/Home';
import Login from '../components/login/Login';
import ForgotPassword from '../components/forgot/ForgotPassword';
import RecoveryPassword from '../components/forgot/RecoveryPassword';
import Register from '../components/register/Register';
import ActivateAccount from '../components/register/ActivateAccount';
import BillingChrome from '../components/chromes/BillingChrome';
import BillingSummary from '../components/billings/BillingSummary';
import TeamMemberBrowser from '../components/teams/TeamMemberBrowser';
import TeamDeactivate from '../components/teams/TeamDeactivate';
import TeamSetting from '../components/teams/TeamSetting';
import TeamMemberCreator from '../components/teams/TeamMemberCreator';
import TeamMemberEditor from '../components/teams/TeamMemberEditor';
import TeamBrowser from '../components/admin/teams/TeamBrowser';
import AddonCostBrowser from '../components/admin/addons/AddonCostBrowser';
import AddonCostCreator from '../components/admin/addons/AddonCostCreator';
import TeamCreator from '../components/admin/teams/TeamCreator';
import TeamEditor from '../components/admin/teams/TeamEditor';
import TeamBillingSummary from '../components/admin/teams/TeamBillingSummary';
import CreditTransactionBrowser from '../components/admin/transactions/CreditTransactionBrowser';
import Page403 from '../components/errors/Page403';
import Page404 from '../components/errors/Page404';
import PageUsageReport from '../components/billings/pages/PageUsageReport';
import PageOrderNumberReport from '../components/billings/pages/PageOrderNumberReport';
import PaymentResponse from '../components/billings/payments/PaymentResponse';
import Profile from '../components/members/Profile';
import EditPassword from '../components/members/EditPassword';
import ManageClientUsageHistory from '../components/admin/billings/ManageClientUsageHistory';
import Support from '../components/Support';
import guard from './guard';

const authManager = new AuthManager();

const routes = [
  {
    path: '/',
    redirect: '/login',
  },
  {
    path: '/login',
    component: Login,
  },
  {
    path: '/register',
    component: Register,
  },
  {
    path: '/:namespace/login',
    component: Login,
  },
  {
    path: '/:namespace/forgot',
    component: ForgotPassword,
  },
  {
    path: '/:namespace/activate/:token',
    component: ActivateAccount,
  },
  {
    path: '/:namespace/recovery/:token',
    component: RecoveryPassword,
  },
  {
    path: '/:namespace/403',
    component: Page403,
  },
  {
    path: '/:namespace/404',
    component: Page404,
  },
  {
    path: '/:namespace/usage-report',
    component: PageUsageReport,
    meta: { requiresAuth: true },
  },
  {
    path: '/:namespace/transactions',
    component: PageOrderNumberReport,
    meta: { requiresAuth: true },
  },
  {
    path: '/:namespace/billing/payment/:id',
    component: PaymentResponse,
    meta: { requiresAuth: true },
  },
  {
    path: '/:namespace/admin/teams/:subnamespace/usage-report',
    component: PageUsageReport,
    meta: { requiresAuth: true }, //TODO: implement requiresSuperAdmin
  },
  {
    path: '/:namespace/admin/teams/:subnamespace/transactions',
    component: PageOrderNumberReport,
    meta: { requiresAuth: true }, //TODO: implement requiresSuperAdmin
  },
  {
    path: '/:namespace/reseller/teams/:subnamespace/usage-report',
    component: PageUsageReport,
    meta: { requiresAuth: true }, //TODO: implement requiresSuperAdmin
  },
  {
    path: '/:namespace',
    component: AppChrome,
    redirect: '/:namespace/dashboard',
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        component: Home,
      },
      {
        path: 'billing',
        redirect: '',
        component: BillingChrome,
        children: [
          {
            path: '',
            component: BillingSummary,
          },
        ],
      },
      {
        path: 'team',
        component: TeamChrome,
        redirect: 'team/members',
        meta: { requiresManager: true },
        children: [
          {
            path: 'members',
            component: TeamMemberBrowser,
          },
          {
            path: 'members/create',
            component: TeamMemberCreator,
          },
          {
            path: 'members/:memberId/edit',
            component: TeamMemberEditor,
          },
          {
            path: 'deactivate',
            component: TeamDeactivate,
          },
          {
            path: 'setting',
            component: TeamSetting,
          },
        ],
      },
      {
        path: 'self/profile',
        component: Profile,
      },
      {
        path: 'self/password',
        component: EditPassword,
      },
      {
        path: 'support',
        component: Support,
      },
      {
        path: 'logout',
        beforeEnter: function(to, from, next) {
          // clear token
          authManager.logout(to.params.namespace).then(() => {
            // redirect to login page
            next(`/${to.params.namespace}/login`);
          });
        },
      },
      {
        path: 'admin',
        component: AdminChrome,
        redirect: 'admin/teams',
        meta: { requiresManager: true },
        children: [
          {
            path: 'teams',
            component: TeamBrowser,
          },
          {
            path: 'teams/create',
            component: TeamCreator,
          },
          {
            path: 'teams/:subnamespace/edit',
            component: TeamEditor,
          },
          {
            path: 'teams/:subnamespace/members/create',
            component: TeamMemberCreator,
          },
          {
            path: 'teams/:subnamespace/members/:memberId/edit',
            component: TeamMemberEditor,
          },
          {
            path: 'teams/:subnamespace/billings',
            component: TeamBillingSummary,
          },
          {
            path: 'client-usage-history',
            component: ManageClientUsageHistory,
          },
          {
            path: 'addon-cost',
            component: AddonCostBrowser,
          },
          {
            path: 'addon-cost/create',
            component: AddonCostCreator,
          },
          {
            path: 'transactions',
            component: CreditTransactionBrowser,
          },
        ],
      },
      {
        path: 'reseller',
        component: AdminChrome,
        redirect: 'reseller/teams',
        meta: { requiresSuperAdmin: false },
        children: [
          {
            path: 'teams',
            component: TeamBrowser,
          },
          {
            path: 'teams/create',
            component: TeamCreator,
          },
          {
            path: 'teams/:subnamespace/edit',
            component: TeamEditor,
          },
          {
            path: 'teams/:subnamespace/members/create',
            component: TeamMemberCreator,
          },
          {
            path: 'teams/:subnamespace/members/:memberId/edit',
            component: TeamMemberEditor,
          },
          {
            path: 'teams/:subnamespace/billings',
            component: TeamBillingSummary,
          },
        ],
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior () {
    return { x: 0, y: 0 };
  },
});

// guard routes
router.beforeEach(guard);

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js